import { useTranslation } from 'react-i18next';
import { Check, Database } from 'lucide-react';
import { Button, Menu } from '../ui';
import { useKnowledgeScope } from '../../context/NavigationContext';
import { useSession } from '../../context/SessionContext';

const SCOPES = [
  { id: 'company', labelKey: 'chat.knowledgeScopeCompany' },
  { id: 'all', labelKey: 'chat.knowledgeScopeAll' },
];

/**
 * Knowledge scope + connected source, shown in the mobile top bar (spec §8.4).
 *
 * "Firmenwissen" restricts answers to verifiable company sources, "Alles"
 * additionally allows general model knowledge — the visible half of the trust
 * contract (spec §11.2). The connected source is spelled out next to it so
 * the "No Source" state is never silent.
 *
 * @param {object} props
 * @param {'topbar'|'inline'} [props.placement='topbar']
 */
export function KnowledgeScopePicker({ placement = 'topbar' }) {
  const { t } = useTranslation();
  const { scope, setScope } = useKnowledgeScope();
  const { sessionMode, workspacePath } = useSession();

  const sourceName =
    sessionMode === 'axiom'
      ? 'Axiom'
      : sessionMode === 'catalog'
        ? 'Catalog'
        : workspacePath
          ? workspacePath.split('/').filter(Boolean).pop()
          : null;

  const active = SCOPES.find((entry) => entry.id === scope) ?? SCOPES[0];

  const items = SCOPES.map((entry) => ({
    id: entry.id,
    label: t(entry.labelKey),
    icon:
      entry.id === scope ? (
        <Check size={16} aria-hidden="true" />
      ) : (
        <span className="etch-shell-scope__spacer" aria-hidden="true" />
      ),
    onSelect: () => setScope(entry.id),
  }));

  return (
    <Menu
      align="end"
      items={items}
      trigger={(triggerProps) => (
        <Button
          {...triggerProps}
          variant="ghost"
          size="compact"
          className="etch-shell-scope"
          data-placement={placement}
          aria-label={`${t('chat.knowledgeScope')}: ${t(active.labelKey)} — ${sourceName ?? t('states.noSource')}`}
        >
          <Database size={16} aria-hidden="true" />
          <span className="etch-shell-scope__label">{t(active.labelKey)}</span>
          <span className="etch-shell-scope__source">{sourceName ?? t('states.noSource')}</span>
        </Button>
      )}
    />
  );
}

export default KnowledgeScopePicker;
